import { Shape, ShapeType } from '@/services/shape';
import { useImagesStore } from '@/store/use-images-store';
import { loadImageDom } from '@/utils/fs/image.utils';
import { mainRenderer } from './use-canvas-render';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { WatchfaceService } from '@/services/watchface';
import { ElectronApi } from '@/services/electron.api';

export const useImageDoms = () => {
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();
  const watchfaceStore = useWatchfaceStore();
  const _getNotFirstImages = (watchfaceAst?: Ast) => {
    const notFirstImages: string[] = [];
    if (!watchfaceAst) return notFirstImages;

    const resourceAst = watchfaceAst.children.find(
      (item) => item.tag == 'Resources'
    );
    const spriteAsts = resourceAst?.children.filter(
      (item) => item.tag == 'Sprite'
    );
    const firstFrameSrcs: string[] = [];
    spriteAsts?.forEach((item) => {
      const ref = item.attrsMap['ref'];
      const imageArrayAst = resourceAst?.children.find(
        (item) => '@' + item.attrsMap.name == ref
      );
      const allRefs = resourceAst?.children.filter(
        (it) => it.attrsMap.name == '@' + imageArrayAst?.attrsMap.name
      );
      // 仅当该 ImageArray 仅被 Sprite 引用 时，我们可以安全地将非首帧视为可忽略,但是当同组数据也被其他元素引用时，就不能忽略非首帧图片了
      const exclusivelyReferencedBySprite =
        allRefs &&
        allRefs.length > 0 &&
        allRefs.every((it) => it.tag == 'Sprite');
      if (!exclusivelyReferencedBySprite) return;
      const defaultIndex = Number(item.attrsMap['_defaultIndex'] ?? 0);

      imageArrayAst?.children.forEach((item, index) => {
        const src = item.attrsMap.src;
        if (index == defaultIndex) firstFrameSrcs.push(src);
        if (index != defaultIndex) notFirstImages.push(src);
      });
    });
    return [
      ...new Set(notFirstImages.filter((src) => !firstFrameSrcs.includes(src)))
    ];
  };
  const imgsToDoms = async (data: {
    imgs: StoreResourceItem[];
    watchfaceAst?: Ast;
    cached?: {
      [key: string]: HTMLImageElement;
    };
    wholeImagesframeSrcs?: string[];
  }) => {
    const { watchfaceAst, imgs, cached, wholeImagesframeSrcs } = data;
    const res: {
      [key: string]: HTMLImageElement;
    } = {};
    // 获取所有序列帧图组的图片srcs，非第一帧
    const notFirstImages = _getNotFirstImages(watchfaceAst);

    const promises: Promise<undefined>[] = [];
    imgs.forEach((img) => {
      if (
        notFirstImages.includes(img.basename) &&
        !wholeImagesframeSrcs?.includes(img.basename)
      ) {
        return;
      }
      promises.push(
        (async () => {
          try {
            const dom =
              cached?.[img.basename] ?? (await loadImageDom(img.value ?? ''));
            if (dom) res[img.basename] = dom;
          } catch (error) {
            // console.log(error);
          }
        })()
      );
    });
    await Promise.all(promises);
    return res;
  };

  /**
   * 获取imageStore里的图片doms
   * @param data
   * @returns
   */
  const getImgDoms = async (data: {
    /**
     * 要获取的图片所属theme
     */
    themeId: string;
    colorId: string;
    /**
     * 针对序列帧，不会全部获取图片，默认获取需要展示的图片，如果需要展示全部图片，传入keepWholeImagesSpriteIds
     */
    keepWholeImagesSpriteIds?: string[];
  }) => {
    const { themeId, colorId: _colorId, keepWholeImagesSpriteIds = [] } = data;
    const res: {
      [key: string]: HTMLImageElement;
    } = {};

    const theme = layerStore.findTheme(themeId);
    // 如果是recolor模式，获取当前theme的第一个colorId（因为其他配色下不存在资源）
    const colorId =
      watchfaceStore.colorMode == 'recolor'
        ? theme?.colorGroupTable?.[0].id ?? ''
        : _colorId;

    // 获取需要处理的资源
    const imgBasenames: string[] = [];
    const _getImages = (layers: StoreLayerItem[]) => {
      layers.forEach((item) => {
        const shape = Shape.getShape(item.id, item);
        if (!shape) return;
        if ([ShapeType.Widget, ShapeType.Slot].includes(shape.type)) {
          _getImages(item.children ?? []);
        } else {
          const { spriteType = 'sprite' } = shape.attrs;
          Object.values(shape.attrs.resources ?? {}).forEach((item) => {
            const images = item[colorId] ?? [];

            // 序列帧 需要获取所需图片
            if (
              shape.type == ShapeType.Sprite &&
              spriteType == 'sprite' &&
              // 非获取序列帧全集
              !keepWholeImagesSpriteIds.includes(shape.id)
            ) {
              const index =
                shape.getSpriteMulitItems(true)?.[0]?.spriteDefaultIndex ?? 0;

              imgBasenames.push(images[index]);
            } else {
              imgBasenames.push(...images);
            }
          });
        }
      });
    };
    _getImages(theme?.children ?? []);

    const cachedImageDoms = mainRenderer?.runtime.images;

    const promises: Promise<undefined>[] = [];
    imgBasenames.forEach((imgBasename) => {
      const { basename, value = '' } = imageStore.imagesMap[imgBasename] ?? {};
      if (!basename || !value) return;

      promises.push(
        (async () => {
          try {
            let dom: HTMLImageElement | undefined | null =
              cachedImageDoms?.[basename];
            if (!dom) {
              dom = await loadImageDom(value);
            }
            if (dom) res[basename] = dom;
          } catch (error) {
            // console.log(error);
          }
        })()
      );
    });
    await Promise.all(promises);
    return res;
  };

  const _getImageSrcsFromAst = (data: {
    watchfaceAst: Ast;
    /**
     * 针对序列帧，不会全部获取图片，默认获取需要展示的图片，如果需要展示全部图片，传入keepWholeImagesSpriteIds
     */
    keepWholeImagesSpriteIds?: string[];
    watchfacePath?: string;
  }) => {
    const { watchfaceAst, watchfacePath, keepWholeImagesSpriteIds } = data;

    const path = ElectronApi.path;
    const resourcePath = new WatchfaceService(watchfacePath).ResourcePath;
    const resourcesAst = watchfaceAst.children.find(
      (item) => item.tag == 'Resources'
    );

    const videoSrcs: { basename: string; realpath: string }[] = [];
    const imageSrcs: { basename: string; realpath: string }[] = [];

    const _addImageSrc = (src: string) => {
      imageSrcs.push({
        basename: src,
        realpath: path?.join(resourcePath, src) ?? ''
      });
    };

    resourcesAst?.children.forEach((item) => {
      if (item.tag == 'File' && item.attrsMap.fileName?.endsWith('.mp4')) {
        const src = item.attrsMap.fileName;
        videoSrcs.push({
          basename: src,
          realpath: path?.join(resourcePath, src) ?? ''
        });
      }
      if (item.tag == 'Image') {
        const src = item.attrsMap.src;
        _addImageSrc(src);
      }
      // 如图是图组
      if (item.tag == 'ImageArray') {
        const imageArrayAst = item;

        // 查找引用该图组的元素
        const spriteAsts = resourcesAst?.children.filter((item2) => {
          return item2.attrsMap.ref == '@' + imageArrayAst.attrsMap.name;
        });
        if (!spriteAsts.length) return;

        // 是否单序列帧引用，且不需要获取全部图片
        const isSprite = spriteAsts.every((item) => {
          return item.tag == 'Sprite';
        });

        if (isSprite) {
          // 如果单序列帧引用，则获取第一个图片的dom
          // 获取序列帧默认显示的图片
          spriteAsts.forEach((spriteAst) => {
            const index = Number(spriteAst.attrsMap._defaultIndex ?? 0);
            const src = item.children[index].attrsMap.src;
            _addImageSrc(src);
          });
        } else {
          // 如果不是单序列帧引用，则获取全部图片
          imageArrayAst.children.forEach((image) => {
            const src = image.attrsMap.src;
            _addImageSrc(src);
          });
        }
      }
    });

    return { videoSrcs, imageSrcs };
  };

  return {
    /**
     * 获取图片的doms，如果是序列帧只获取第一个图片的dom
     * @param imgs 【可选】如不提供，用当前image store里的数据
     * @returns
     */
    imgsToDoms,
    getImgDoms
  };
};
